// MathAid.tsx - the app-shown price plus the FADING math scaffold (North Star §5). The app always shows
// the headline price (`big`: a required-equity % postflop, "Iso" preflop). The EXPLANATION fades in
// three steps:
//   full      → the worked sentence (why the number is what it is), terms tap-to-learn.
//   shorthand → the pot-odds trick only ("$30 to win $103, 30/103 ≈ 29%").
//   off       → the headline number alone. You are expected to work out the why yourself.
//
// In v1 the level is user-controllable here (the segmented control). The eventual auto-fade is GATED ON
// ARITHMETIC ACCURACY from the progress engine (deferred, not wired yet) - this control is the manual
// stand-in so the fade is usable and demonstrable now. The do-your-own-math principle is never violated:
// the app shows the PRICE, but at "off" it stops explaining it.
import { Term } from '../components/Glossary';
import type { PriceDef, PriceToken } from './data';

export type AidLevel = 'full' | 'shorthand' | 'off';

const AID_LEVELS: { level: AidLevel; label: string }[] = [
  { level: 'full', label: 'Full' },
  { level: 'shorthand', label: 'Shorthand' },
  { level: 'off', label: 'Off' },
];

/** Render a price-explanation token run: prose + tap-to-learn terms. */
function PriceTokens({ tokens }: { tokens: PriceToken[] }) {
  return (
    <>
      {tokens.map((t, i) =>
        typeof t === 'string' ? (
          <span key={i}>{t}</span>
        ) : (
          <Term key={i} term={t.term}>
            {t.text}
          </Term>
        ),
      )}
    </>
  );
}

export interface MathAidProps {
  price: PriceDef;
  level: AidLevel;
  onLevel: (level: AidLevel) => void;
}

export function MathAid({ price, level, onLevel }: MathAidProps) {
  return (
    <div className="sec">
      <div className="stt-row">
        <span className="bar" />
        <div>
          <div className="stt">{price.title}</div>
          <div className="sd">This one the app shows you. How much it explains is up to you.</div>
        </div>
      </div>

      {/* fading math-aid control */}
      <div className="aidseg" role="group" aria-label="Math aid detail">
        {AID_LEVELS.map((a) => (
          <button
            key={a.level}
            type="button"
            className={'aidbtn' + (level === a.level ? ' on' : '')}
            aria-pressed={level === a.level}
            onClick={() => onLevel(a.level)}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className="price">
        <div className="big num">{price.big}</div>
        {level !== 'off' && (
          <div className="exp">
            {level === 'full' ? <PriceTokens tokens={price.full} /> : price.shorthand}
          </div>
        )}
      </div>
    </div>
  );
}
